/**
 * Directive names this schema models by name. Anything else round-trips through
 * `genericDirective`, so an unrecognised directive is preserved rather than lost.
 *
 * Adding a block to the product means adding it here and in `pm-schema.ts`, then
 * the round-trip property test covers it automatically.
 *
 * The name says "container" because that is what all of these were until
 * `button` and `file`. `button` is written as a LEAF directive (`::button[Get
 * started]{href="/x"}`) because its label is a single line of phrasing content,
 * not a block — but it is "known" in exactly the same sense as `hint` or
 * `figure`, so it lives in the same map rather than a second one that every
 * caller of `isKnownDirective` would also have to check.
 */
export const CONTAINER_DIRECTIVES = {
    hint: 'hint',
    tabs: 'tabs',
    tab: 'tab',
    expandable: 'expandable',
    cards: 'cards',
    card: 'card',
    steps: 'steps',
    step: 'step',
    figure: 'figure',
    embed: 'embed',
    file: 'file',
    button: 'button',
    codegroup: 'codegroup',
    snippet: 'snippet',
    /** One parameter, header, or response field of an API reference. */
    field: 'field',
};
export function isKnownDirective(name) {
    return Object.prototype.hasOwnProperty.call(CONTAINER_DIRECTIVES, name);
}
/**
 * Inline (`textDirective`) names this schema models by name — `:name[text]`,
 * the one-colon form. This is Markdown's only way to write an inline mark that
 * is not already GFM: there is no bare syntax for underline (no rival meaning
 * to protect, unlike `_x_`) or highlight, so both are spelled as directives
 * rather than invented delimiters that would silently corrupt unrelated text
 * containing `==` or the like.
 *
 * Anything else round-trips through `unknownInline`, same contract as an
 * unrecognised container directive.
 */
export const TEXT_DIRECTIVES = {
    underline: 'underline',
    highlight: 'highlight',
};
export function isKnownTextDirective(name) {
    return Object.prototype.hasOwnProperty.call(TEXT_DIRECTIVES, name);
}
/** Valid `type` values for `:::hint`. Anything else is normalised to `note`. */
export const HINT_TYPES = ['note', 'tip', 'warning', 'danger', 'info'];
export function normaliseHintType(value) {
    return typeof value === 'string' && HINT_TYPES.includes(value)
        ? value
        : 'note';
}
/**
 * The vocabulary has a version, and old spellings keep opening.
 *
 * The document format here is Markdown, not a stored JSON blob, so "schema
 * migration" does not mean rewriting rows — it means that a page written a year
 * ago, by an older editor or by a person typing directives by hand, still opens
 * as the blocks it was meant to be. Two things make that true.
 *
 * The VERSION is a decision point rather than a number anybody reads. A test
 * holds it against the block list, so adding or renaming a block fails until
 * somebody bumps it — and bumping it is the moment to ask what happens to the
 * pages that already exist.
 *
 * The ALIASES are the answer for the spellings we have to accept. Some are
 * historical; most are what a person types because another tool taught them to
 * — `:::warning` is a callout in Docusaurus, VitePress and Mintlify, and
 * somebody who writes it here means a callout. Reading it as `genericDirective`
 * is technically lossless and practically a bug: the editor shows an opaque
 * block, the site renders nothing, and nobody can see why.
 *
 * An alias is read, never written. Opening a page does not rewrite it; saving
 * writes the canonical spelling, because the editor holds blocks rather than
 * text. That asymmetry is deliberate: an import or a hand-written page is not
 * silently reformatted, and an edited one is normalised once.
 */
export const VOCABULARY_VERSION = 1;
/** What each accepted alias means, and the attributes it implies. */
export const DIRECTIVE_ALIASES = {
    // The four callout spellings every other documentation tool uses.
    note: { name: 'hint', attributes: { type: 'note' } },
    info: { name: 'hint', attributes: { type: 'note' } },
    tip: { name: 'hint', attributes: { type: 'tip' } },
    warning: { name: 'hint', attributes: { type: 'warning' } },
    caution: { name: 'hint', attributes: { type: 'warning' } },
    /*
     * `danger`, not `caution`. `caution` is not a `HintType`, so
     * `normaliseHintType` fell through to `note` — the strongest callout every
     * other tool has, the one an imported page uses for "this deletes your
     * data", rendered as the mildest. And permanently: the alias resolves when a
     * page is opened in the block editor, so the first edit rewrote the file to
     * `:::hint{type="caution"}` and the original spelling was gone.
     *
     * The other five spellings were all right. `:::caution` above maps to
     * `warning` deliberately — the word sits between a warning and a danger, and
     * every tool disagrees about which — and that one IS a legal type.
     */
    danger: { name: 'hint', attributes: { type: 'danger' } },
    // VitePress writes a code group with a hyphen, and a collapsed block as
    // `details`. Both are exactly our blocks under another name.
    'code-group': { name: 'codegroup' },
    details: { name: 'expandable' },
    // Mintlify's names for the same two blocks.
    accordion: { name: 'expandable' },
    codegroup: { name: 'codegroup' },
};
/**
 * The canonical directive for a name, with the attributes its alias implies.
 *
 * Returns null for a name that is neither known nor an alias — which stays
 * `genericDirective` and round-trips untouched, because a directive somebody
 * invented for their own renderer is theirs.
 */
export function resolveDirective(name, attributes = {}) {
    if (isKnownDirective(name))
        return { name, attributes };
    const alias = DIRECTIVE_ALIASES[name];
    if (!alias)
        return null;
    // The author's own attributes win: `:::warning{type="caution"}` means what it
    // says, and the alias only supplies what was left unsaid.
    return { name: alias.name, attributes: { ...(alias.attributes ?? {}), ...attributes } };
}
//# sourceMappingURL=directives.js.map